/**
 * Movement Form
 *
 * Create and edit accounting movements (prima nota).
 */

import React, { useState } from 'react';
import {
  FormField,
  FormRow,
  FormSection,
  FormActions,
  TextInput,
  TextArea,
  Select,
  DateInput,
  CurrencyInput,
} from './FormComponents';
import { chartOfAccounts } from '@business-assistant/shared/data/accounting';
import type { Movement, MovementDirection } from '@business-assistant/shared/data/types';

// =============================================================================
// TYPES
// =============================================================================

interface MovementFormData {
  date: string;
  description: string;
  amount: number;
  direction: MovementDirection;
  accountId: string;
  counterparty: string;
  notes: string;
}

interface MovementFormProps {
  movement?: Movement;
  onSave: (data: MovementFormData) => void;
  onCancel: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DIRECTION_OPTIONS = [
  { value: 'debit', label: 'Dare (Addebito)' },
  { value: 'credit', label: 'Avere (Accredito)' },
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function MovementForm({ movement, onSave, onCancel }: MovementFormProps) {
  const isEditing = !!movement;

  const [formData, setFormData] = useState<MovementFormData>({
    date: movement?.date || new Date().toISOString(),
    description: movement?.description || '',
    amount: movement?.amount || 0,
    direction: movement?.direction || 'debit',
    accountId: movement?.accountId || '',
    counterparty: movement?.counterparty || '',
    notes: movement?.notes || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Account options (only leaf accounts)
  const accountOptions = chartOfAccounts
    .filter((a) => a.isLeaf && a.isActive)
    .map((a) => ({
      value: a.id,
      label: `${a.code} - ${a.name}`,
    }));

  // Group accounts by category for display
  const accountsByCategory = chartOfAccounts
    .filter((a) => a.isLeaf && a.isActive)
    .reduce((acc, account) => {
      if (!acc[account.category]) {
        acc[account.category] = [];
      }
      acc[account.category].push({
        value: account.id,
        label: `${account.code} - ${account.name}`,
      });
      return acc;
    }, {} as Record<string, { value: string; label: string }[]>);

  // Handlers
  const updateField = <K extends keyof MovementFormData>(
    field: K,
    value: MovementFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Data obbligatoria';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Descrizione obbligatoria';
    }
    if (formData.amount <= 0) {
      newErrors.amount = 'Importo deve essere maggiore di zero';
    }
    if (!formData.accountId) {
      newErrors.accountId = 'Conto obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--bg, white)',
          borderRadius: '0.75rem',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isEditing ? 'Modifica Movimento' : 'Nuovo Movimento'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
            }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          <FormSection title="Dati Movimento">
            <FormRow>
              <FormField label="Data" required error={errors.date}>
                <DateInput
                  value={formData.date}
                  onChange={(v) => updateField('date', v)}
                />
              </FormField>
              <FormField label="Tipo" required>
                <Select
                  value={formData.direction}
                  onChange={(v) => updateField('direction', v as MovementDirection)}
                  options={DIRECTION_OPTIONS}
                />
              </FormField>
            </FormRow>

            <FormField label="Descrizione" required error={errors.description}>
              <TextInput
                value={formData.description}
                onChange={(v) => updateField('description', v)}
                placeholder="Descrizione del movimento..."
              />
            </FormField>

            <FormRow>
              <FormField label="Importo" required error={errors.amount}>
                <CurrencyInput
                  value={formData.amount}
                  onChange={(v) => updateField('amount', v)}
                />
              </FormField>
              <FormField label="Controparte">
                <TextInput
                  value={formData.counterparty}
                  onChange={(v) => updateField('counterparty', v)}
                  placeholder="Nome controparte"
                />
              </FormField>
            </FormRow>

            <FormField label="Conto" required error={errors.accountId}>
              <Select
                value={formData.accountId}
                onChange={(v) => updateField('accountId', v)}
                options={accountOptions}
                placeholder="Seleziona conto..."
              />
            </FormField>
          </FormSection>

          <FormSection title="Note">
            <TextArea
              value={formData.notes}
              onChange={(v) => updateField('notes', v)}
              placeholder="Note aggiuntive..."
              rows={3}
            />
          </FormSection>

          <FormActions
            onCancel={onCancel}
            onSubmit={handleSubmit}
            submitLabel={isEditing ? 'Salva Modifiche' : 'Registra Movimento'}
          />
        </div>
      </div>
    </div>
  );
}

export type { MovementFormData };
